import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Check, Mail, Waves, Compass } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SITE_OPTIONS = [
  { value: "site_one", label: "Site One", hint: "3.5 acres · 175 ft waterfront · Opens 2027 season" },
  { value: "site_two", label: "Site Two", hint: "15 acres · 550 ft waterfront · Longer-term build" },
  { value: "either", label: "Either / Both", hint: "Whichever opens first" },
];

const TIMING_OPTIONS = [
  { value: "august_2026", label: "August / Fall 2026", hint: "Site One launch window" },
  { value: "spring_2027", label: "Spring / Summer 2027", hint: "Site Two launch window" },
  { value: "anytime", label: "Whenever you're ready", hint: "Surprise me — I'm flexible" },
];

export default function WaitlistPage() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const prefilledSite = params.get("site");
  const initialInterest = prefilledSite === "site-one" ? "site_one"
    : prefilledSite === "site-two" ? "site_two"
    : "either";

  const [form, setForm] = useState({
    name: "", email: "", phone: "", group_size: "",
    interested_in: initialInterest,
    preferred_timing: initialInterest === "site_one" ? "august_2026" : initialInterest === "site_two" ? "spring_2027" : "anytime",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // keep timing sane when user flips site
    if (form.interested_in === "site_one" && form.preferred_timing === "spring_2027") {
      setForm(f => ({ ...f, preferred_timing: "august_2026" }));
    }
    if (form.interested_in === "site_two" && form.preferred_timing === "august_2026") {
      setForm(f => ({ ...f, preferred_timing: "spring_2027" }));
    }
  }, [form.interested_in, form.preferred_timing]);

  const handleChange = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const payload = {
        ...form,
        group_size: form.group_size ? parseInt(form.group_size, 10) : null,
      };
      await axios.post(`${API}/waitlist`, payload);
      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again or email whalecoveoffgrid@gmail.com directly.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="pt-16 min-h-screen bg-wc-cream" data-testid="waitlist-success">
        <div className="max-w-xl mx-auto px-6 py-24 text-center">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
            className="w-16 h-16 mx-auto mb-6 rounded-full bg-wc-teal/15 flex items-center justify-center">
            <Check className="w-8 h-8 text-wc-teal" />
          </motion.div>
          <h1 className="font-display text-wc-forest text-3xl sm:text-4xl font-semibold mb-4">
            You're on the list.
          </h1>
          <p className="font-body text-wc-slate text-base leading-relaxed mb-8">
            Thanks for reaching out, {form.name.split(" ")[0] || "friend"}. We'll email you personally as soon as sites open up — no form letters, no bulk blasts. Just a note from us to you.
          </p>
          <a href="/" className="inline-flex items-center gap-2 font-body text-wc-teal text-sm hover:text-wc-forest transition-colors">
            <Waves className="w-4 h-4" /> Back to Whale Cove
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 min-h-screen bg-wc-cream" data-testid="waitlist-page">
      {/* Header */}
      <section className="wc-section bg-wc-cream pb-0">
        <div className="max-w-3xl mx-auto">
          <p className="font-accent italic text-wc-teal text-base mb-3">Follow the Build</p>
          <h1 className="font-display text-wc-forest text-4xl sm:text-5xl lg:text-6xl font-bold mb-5 leading-tight">
            We're building it. Come along for the ride.
          </h1>
          <p className="font-body text-wc-slate text-base sm:text-lg leading-relaxed max-w-2xl">
            <span className="text-wc-forest font-medium">Site One</span> opens for the 2027 season. Through 2026 we're on the land ourselves — testing every detail with family and friends, walking permits through Digby Municipality. <span className="text-wc-forest font-medium">Site Two</span> is a longer-term build (the access road is a major project). Leave your details and you'll be first in line when the gates open.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="wc-section bg-wc-cream pt-10">
        <div className="max-w-2xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-6 bg-white/60 border border-wc-teal/15 rounded-lg p-6 sm:p-8" data-testid="waitlist-form">

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="wl-name" className="font-body text-wc-forest text-xs tracking-widest uppercase mb-1.5 block">Name</Label>
                <Input id="wl-name" required value={form.name} onChange={handleChange("name")}
                  className="bg-white border-wc-teal/30 text-wc-forest focus-visible:ring-wc-teal" data-testid="waitlist-name" />
              </div>
              <div>
                <Label htmlFor="wl-email" className="font-body text-wc-forest text-xs tracking-widest uppercase mb-1.5 block">Email</Label>
                <Input id="wl-email" type="email" required value={form.email} onChange={handleChange("email")}
                  className="bg-white border-wc-teal/30 text-wc-forest focus-visible:ring-wc-teal" data-testid="waitlist-email" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="wl-phone" className="font-body text-wc-forest text-xs tracking-widest uppercase mb-1.5 block">Phone <span className="text-wc-slate/60 normal-case tracking-normal">(optional)</span></Label>
                <Input id="wl-phone" type="tel" value={form.phone} onChange={handleChange("phone")}
                  className="bg-white border-wc-teal/30 text-wc-forest focus-visible:ring-wc-teal" data-testid="waitlist-phone" />
              </div>
              <div>
                <Label htmlFor="wl-group" className="font-body text-wc-forest text-xs tracking-widest uppercase mb-1.5 block">Group Size <span className="text-wc-slate/60 normal-case tracking-normal">(optional)</span></Label>
                <Input id="wl-group" type="number" min="1" max="20" value={form.group_size} onChange={handleChange("group_size")}
                  className="bg-white border-wc-teal/30 text-wc-forest focus-visible:ring-wc-teal" data-testid="waitlist-group" />
              </div>
            </div>

            {/* Interested in site */}
            <div>
              <Label className="font-body text-wc-forest text-xs tracking-widest uppercase mb-3 block">Which site are you interested in?</Label>
              <div className="space-y-2">
                {SITE_OPTIONS.map(opt => (
                  <label key={opt.value} data-testid={`waitlist-site-${opt.value}`}
                    className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-all ${
                      form.interested_in === opt.value
                        ? "border-wc-teal bg-wc-teal/5"
                        : "border-wc-teal/15 hover:border-wc-teal/40 bg-white/50"
                    }`}>
                    <input type="radio" name="interested_in" value={opt.value}
                      checked={form.interested_in === opt.value}
                      onChange={handleChange("interested_in")}
                      className="mt-1 accent-wc-teal" />
                    <div>
                      <p className="font-body text-wc-forest text-sm font-medium">{opt.label}</p>
                      <p className="font-body text-wc-slate text-xs mt-0.5">{opt.hint}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Preferred timing */}
            <div>
              <Label className="font-body text-wc-forest text-xs tracking-widest uppercase mb-3 block">When are you hoping to visit?</Label>
              <div className="space-y-2">
                {TIMING_OPTIONS.map(opt => (
                  <label key={opt.value} data-testid={`waitlist-timing-${opt.value}`}
                    className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-all ${
                      form.preferred_timing === opt.value
                        ? "border-wc-teal bg-wc-teal/5"
                        : "border-wc-teal/15 hover:border-wc-teal/40 bg-white/50"
                    }`}>
                    <input type="radio" name="timing" value={opt.value}
                      checked={form.preferred_timing === opt.value}
                      onChange={handleChange("preferred_timing")}
                      className="mt-1 accent-wc-teal" />
                    <div>
                      <p className="font-body text-wc-forest text-sm font-medium">{opt.label}</p>
                      <p className="font-body text-wc-slate text-xs mt-0.5">{opt.hint}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Message */}
            <div>
              <Label htmlFor="wl-message" className="font-body text-wc-forest text-xs tracking-widest uppercase mb-1.5 block">
                Anything you'd like us to know? <span className="text-wc-slate/60 normal-case tracking-normal">(optional)</span>
              </Label>
              <Textarea id="wl-message" rows={4} value={form.message} onChange={handleChange("message")}
                placeholder="What draws you to Whale Cove? Anniversary, photography trip, family retreat..."
                className="bg-white border-wc-teal/30 text-wc-forest focus-visible:ring-wc-teal resize-none"
                data-testid="waitlist-message" />
            </div>

            {error && <p className="font-body text-red-600 text-sm">{error}</p>}

            <Button type="submit" disabled={loading}
              className="w-full bg-wc-forest text-wc-cream hover:bg-wc-teal font-body text-sm tracking-wide py-6 rounded-full"
              data-testid="waitlist-submit">
              {loading ? "Sending..." : "Add me to the list"}
            </Button>

            <p className="font-body text-wc-slate/70 text-xs text-center leading-relaxed">
              We'll only use this to contact you about Whale Cove — never shared, never sold.
            </p>
          </form>

          {/* Secondary contact */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-6 text-center">
            <a href="mailto:whalecoveoffgrid@gmail.com" className="inline-flex items-center gap-2 font-body text-wc-slate text-sm hover:text-wc-forest transition-colors">
              <Mail className="w-4 h-4" /> whalecoveoffgrid@gmail.com
            </a>
            <span className="hidden sm:inline text-wc-slate/30">·</span>
            <span className="inline-flex items-center gap-2 font-body text-wc-slate text-sm">
              <Compass className="w-4 h-4" /> Digby Neck, Nova Scotia
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
